<template>
  <div class="u">
    <div v-if="deleteBusy" class="delete-overlay" aria-live="polite" aria-busy="true">
      <div class="delete-overlay__card">
        <span class="delete-overlay__spinner" />
        <strong>Bilder werden gelöscht</strong>
      </div>
    </div>
    <div class="source-switch">
      <button class="source-btn" :data-on="uploadSource === 'desktop' ? '1' : '0'" type="button" @click="uploadSource = 'desktop'">
        <Monitor :size="16" />
        Rechner
      </button>
      <button class="source-btn" :data-on="uploadSource === 'phone' ? '1' : '0'" type="button" @click="uploadSource = 'phone'">
        <Smartphone :size="16" />
        Smartphone
      </button>
    </div>

    <section v-if="uploadSource === 'phone'" class="phone-box">
      <div class="phone-head">
        <div>
          <div class="phone-title">Mit dem Handy hochladen</div>
          <div class="phone-sub">QR scannen und Bilder live in dieses Projekt schicken.</div>
        </div>
      </div>

      <div class="phone-qr">
        <img v-if="phoneQrDataUrl" :src="phoneQrDataUrl" alt="QR Code fuer Handy-Upload" />
      </div>

      <div class="phone-actions">
        <button class="btn" type="button" @click="copyPhoneUploadUrl">Link kopieren</button>
        <button class="btn" type="button" @click="syncRemoteProjectUploads">Jetzt synchronisieren</button>
      </div>
    </section>

    <div
      v-if="uploadSource === 'desktop'"
      class="drop"
      :class="{ over: over }"
      @dragover.prevent="over = true"
      @dragleave.prevent="over = false"
      @drop.prevent="onDrop"
      @click="pick"
      role="button"
      tabindex="0"
      @keydown.enter.prevent="pick"
      @keydown.space.prevent="pick"
    >
      <div class="inner">
        <div class="t"><strong>Dateien hierher ziehen</strong> oder klicken</div>
        <div class="s">JPG, PNG, WebP · mehrere Dateien möglich</div>
        <input ref="inp" type="file" multiple :accept="ACCEPT_IMAGE_INPUT" @change="onPick" hidden />
      </div>
    </div>

    <p v-if="err" class="err">{{ err }}</p>

    <section v-if="pool.length" class="pool">
      <div class="pool-head">
        <strong>Bilder im Pool</strong>
        <span>{{ pool.length }}</span>
      </div>

      <div class="pool-grid">
        <article v-for="img in pool" :key="img.id" class="pool-item">
          <button class="pool-preview" type="button" @click="selectFromPool(img.id)">
            <img v-if="previewUrl(img.id)" :src="previewUrl(img.id)!" alt="" loading="lazy" decoding="async" />
            <div v-else class="ph" />
          </button>

          <div class="pool-name" :title="img.name || img.id">{{ img.name || 'Bild' }}</div>

          <div class="row">
            <FlatSelect
              compact
              class="sel-flat"
              :model-value="sizeValue(img) || null"
              :options="sizeSelectOptions"
              placeholder="Größe wählen"
              search-placeholder="Größe suchen…"
              @update:model-value="(v:any) => onSizeChange(img.id, String(v || ''))"
            />
          </div>

          <div class="row" v-for="g in variantGroups" :key="`${img.id}-${g.id}`">
            <select
              class="sel"
              :value="groupValue(img, g.id)"
              @change="onGroupChange(img.id, g, String(($event.target as HTMLSelectElement).value || ''))"
            >
              <option value="">{{ g.title || 'Variante' }} wählen</option>
              <option v-for="o in groupOptions(g)" :key="o.value" :value="o.value">{{ o.label }}</option>
            </select>
          </div>

          <div class="row qty-row">
            <input
              class="qty"
              type="number"
              min="1"
              :value="qtyValue(img)"
              @change="onQtyChange(img.id, Number(($event.target as HTMLInputElement).value || 1))"
            />
          </div>

          <div class="row act-row">
            <button class="btn icon-btn" type="button" @click="duplicateOne(img.id)" aria-label="Duplizieren" title="Duplizieren">
              <Copy :size="16" />
            </button>
            <button
              v-if="isSingleMode"
              class="btn icon-btn"
              type="button"
              @click="removeOne(img.id)"
              aria-label="Löschen"
              title="Löschen"
            >
              <Trash2 :size="16" />
            </button>
            <button
              v-else
              class="btn icon-btn"
              type="button"
              @click="addToCart(img.id)"
              aria-label="In den Warenkorb"
              title="In den Warenkorb"
            >
              <ShoppingCart :size="16" />
            </button>
          </div>
        </article>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, shallowReactive, watch } from 'vue'
import { v4 as uuidv4 } from 'uuid'
import { useImageDB, type ImageRecord } from '../../../../utils/imageDb'
import { createEditorPreview, createThumb, normalizeImageForUpload, readImageDimensions, isHeicBlob } from '../../../../utils/imageOps'
import { useCartStore } from '../../../../store/cart'
import { useProjectStore } from '../../../../store/useProjectStore'
import { useTransientUploadStore } from '../../../../store/useTransientUploadStore'
import { useToast } from '../../../../composables/useToast'
import { deleteImageRecordsRemoteBulk, describeUploadInUse, isUploadInUseError, markRemoteDeleted, uploadImageRecordsBatch, wasRemoteDeleted } from '../../../../services/uploadService'
import { useApolloClient } from '@vue/apollo-composable'
import { getProduct } from '../../../../gql/product/query'
import { mapConfiguratorData } from '../../../../utils/useConfiguratorData'
import FlatSelect, { type FlatSelectOption } from '../../../ui/FlatSelect.vue'
import { Copy, ShoppingCart, Trash2, Monitor, Smartphone, QrCode } from 'lucide-vue-next'
import { getWidgetConfig } from '../../../../runtime/widgetConfig'
import { scheduleUploadsRunnerTick } from '../../../../runtime/uploads-runner.client'
import QRCode from 'qrcode'
import { fetchProjectUploads, invalidateProjectUploadsCache, mapRemoteUploadToRecordMeta, mergeServerAndLocalImageRecord } from '../../../../services/projectUploadBridge'
import { subscribeToUploadEvents } from '../../../../services/uploadEvents'
import { ACCEPT_IMAGE_INPUT, isAcceptedImageUploadFile, MAX_IMAGE_UPLOAD_MB } from '../../../../utils/fileAccept'
import { buildMobileUploadUrl, MOBILE_UPLOAD_QR_OPTIONS } from '../../../../utils/mobileUploadLink'
import { emitUploadEvent } from '../../../../utils/uploadBus'

type ImageSize = { title?: string | null; widthMm?: number; heightMm?: number; variantId?: string }
type ImageOption = { groupId?: string; id?: string; variantId?: string; value?: string }

type SizeOption = {
  id?: string
  variantId?: string
  title?: string
  widthMm?: number
  heightMm?: number
  price?: any
  priceSpecial?: any
  currency?: string
}

type VariantGroup = {
  id: string
  title?: string
  options?: Array<{ id?: string; title?: string; value?: string; price?: any }>
}

const props = defineProps<{
  categoryId: number
  productId: number
  isMultiMode?: boolean
  sizeOptions?: SizeOption[]
  variantGroups?: VariantGroup[]
  preset?: { productId?: number; size?: ImageSize; options?: ImageOption[] }
}>()

const emit = defineEmits<{
  (e: 'uploaded', ids: string[]): void
}>()

const db = useImageDB('imageDB')
const cart = useCartStore()
const project = useProjectStore()
const transientUploads = useTransientUploadStore()
const toast = useToast()
const apollo = useApolloClient().client

const inp = ref<HTMLInputElement | null>(null)
const over = ref(false)
const err = ref('')
const deleteBusy = ref(false)
const pool = ref<ImageRecord[]>([])
const previewMap = shallowReactive<Record<string, string>>({})
const isSingleMode = computed<boolean>(() => !props.isMultiMode)
const uploadSource = ref<'desktop' | 'phone'>('desktop')
const phoneUploadUrl = ref('')
const phoneQrDataUrl = ref('')
const lastRemoteSyncAt = ref<string>('')
let remotePollTimer: ReturnType<typeof setInterval> | null = null
let stopMercure: null | (() => void) = null
const deletedRemoteIds = new Set<string>()
const isUnmounted = ref(false)
const deletedRemoteCleanupTimers = new Set<ReturnType<typeof setTimeout>>()

const variantGroups = computed<VariantGroup[]>(() => (props.variantGroups || []).filter((g: any) => !!g?.id))

const localSizeOptions = ref<SizeOption[]>([])
const effectiveSizeOptions = computed<SizeOption[]>(() => {
  const fromProps = props.sizeOptions || []
  if (fromProps.length) return fromProps
  return localSizeOptions.value
})

const normalizedSizes = computed(() => {
  return (effectiveSizeOptions.value || []).map((o: any, idx: number) => {
    const key = String(o?.variantId || o?.id || `${idx}-${o?.title || 'size'}`)
    return {
      key,
      title: String(o?.title || key),
      variantId: String(o?.variantId || ''),
      widthMm: Number(o?.widthMm || 0),
      heightMm: Number(o?.heightMm || 0),
      price: o?.priceSpecial ?? o?.price ?? null,
      currency: String(o?.currency || 'EUR'),
    }
  })
})

function normalizeSizeTitle(v: any): string {
  return String(v ?? '').trim().toLowerCase()
}

const customSizesFromPool = computed(() => {
  const map = new Map<string, any>()
  for (const rec of pool.value || []) {
    const s = presetOf(rec)?.size || null
    const title = String(s?.title || '').trim()
    if (!title) continue
    const k = normalizeSizeTitle(title)
    if (!k) continue
    if (map.has(k)) continue
    map.set(k, {
      key: `custom:${k}`,
      title,
      variantId: String(s?.variantId || ''),
      widthMm: Number(s?.widthMm || 0),
      heightMm: Number(s?.heightMm || 0),
      price: null,
      currency: 'EUR',
      isCustom: true,
    })
  }
  return Array.from(map.values())
})

const allSizeEntries = computed(() => {
  const base = normalizedSizes.value || []
  const known = new Set(base.map((s: any) => normalizeSizeTitle(s.title)))
  const merged = [...base]
  for (const c of customSizesFromPool.value) {
    if (known.has(normalizeSizeTitle(c.title))) continue
    merged.push(c)
  }
  return merged.sort((a: any, b: any) =>
    (Number(a?.widthMm || 0) - Number(b?.widthMm || 0)) ||
    (Number(a?.heightMm || 0) - Number(b?.heightMm || 0)) ||
    ((a?.price == null ? Number.POSITIVE_INFINITY : Number(a.price)) - (b?.price == null ? Number.POSITIVE_INFINITY : Number(b.price))) ||
    String(a?.title || '').localeCompare(String(b?.title || ''), 'de')
  )
})

const sizeSelectOptions = computed<FlatSelectOption[]>(() => {
  return allSizeEntries.value.map((s: any) => ({
    value: String(s.key),
    label: String(s.title),
    right: s.price != null
      ? formatMoney(s.price, s.currency)
      : (s.isCustom ? 'individuell' : ''),
  }))
})

async function loadSizeOptionsFallback() {
  if ((props.sizeOptions || []).length) return
  const pid = Number(props.productId || 0)
  if (pid <= 0) return
  try {
    const iri = `/api/products/${pid}`
    const { data } = await apollo.query({ query: getProduct, variables: { id: iri }, fetchPolicy: 'network-only' })
    const p = (data as any)?.product ?? null
    const mapped = p ? mapConfiguratorData(p) : null
    const sizeGroup = mapped?.groups?.find((g: any) => g.isSize) || null
    localSizeOptions.value = sizeGroup?.sizeOptions ?? []
  } catch {
    localSizeOptions.value = []
  }
}

function pick() {
  inp.value?.click()
}

async function buildPhoneBridge() {
  const code = await project.ensureProject()
  if (!code) return

  phoneUploadUrl.value = buildMobileUploadUrl({ projectCode: code, categoryId: props.categoryId, productId: Number(props.productId || 0) })
  phoneQrDataUrl.value = await QRCode.toDataURL(phoneUploadUrl.value, MOBILE_UPLOAD_QR_OPTIONS)
}

async function copyPhoneUploadUrl() {
  if (!phoneUploadUrl.value) await buildPhoneBridge()
  if (!phoneUploadUrl.value || typeof navigator === 'undefined' || !navigator.clipboard) return
  await navigator.clipboard.writeText(phoneUploadUrl.value)
}

function previewUrl(id: string) {
  return previewMap[id]
}

function revokePreview(id: string) {
  const url = previewMap[id]
  if (url) URL.revokeObjectURL(url)
  delete previewMap[id]
}

function revokeAllPreviews() {
  for (const id of Object.keys(previewMap)) revokePreview(id)
}

async function ensurePreview(rec: ImageRecord) {
  if (previewMap[rec.id]) return
  const remoteUrl = String((rec as any)?.upload?.remoteUrl || '').trim()
  if (remoteUrl) {
    previewMap[rec.id] = remoteUrl
    return
  }
  const blob = (rec as any).thumbBlob || (rec as any).editorBlob || (rec as any).workingBlob || (rec as any).originalBlob
  if (!blob) return
  previewMap[rec.id] = URL.createObjectURL(blob)
}

function normId(v: any): string {
  const s = String(v ?? '').trim()
  if (!s) return ''
  if (!s.includes('/')) return s
  const parts = s.split('/').filter(Boolean)
  return parts[parts.length - 1] || s
}

function toVariantNumber(v: any): number {
  const s = String(v ?? '').trim()
  if (!s) return 0
  const m = s.match(/(\d+)\s*$/)
  if (m) return Number(m[1]) || 0
  const n = Number(s)
  return Number.isFinite(n) ? n : 0
}

function formatMoney(v: any, currency = 'EUR') {
  const n = typeof v === 'number' ? v : Number(v)
  if (!Number.isFinite(n)) return '—'
  try {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency }).format(n)
  } catch {
    return `${n} ${currency}`
  }
}

async function loadPool() {
  const categoryId = Number(props.categoryId || 0)
  if (categoryId <= 0) {
    pool.value = []
    return
  }
  const dbRows = await db.getByCategory(categoryId)
  const transientRows = transientUploads.listByCategoryId(categoryId)
  const byId = new Map<string, any>()
  for (const row of dbRows || []) byId.set(String((row as any)?.id || ''), row)
  for (const row of transientRows) byId.set(String((row as any)?.id || ''), row)
  const sorted = [...byId.values()].sort((a, b) => Number(b.createdAt || 0) - Number(a.createdAt || 0))
  pool.value = sorted
  for (const rec of sorted) await ensurePreview(rec)
}

async function syncRemoteProjectUploads() {
  const code = await project.ensureProject()
  if (!code) return

  try {
    const cfg = getWidgetConfig()
    invalidateProjectUploadsCache({ editorBaseUrl: cfg.editorBaseUrl, projectCode: code })
    const items = await fetchProjectUploads({
      editorBaseUrl: cfg.editorBaseUrl,
      projectCode: code,
      categoryId: props.categoryId,
      ...(uploadSource.value === 'phone' ? { source: 'phone', editorReady: false } : {}),
      ...(uploadSource.value === 'phone' ? { statuses: ['done'] } : {}),
      since: lastRemoteSyncAt.value || null,
      limit: 50,
    })

    if (!items.length) return

    const known = new Set((pool.value || []).map((x) => String(x.id)))
    for (const item of items.slice().reverse()) {
      const id = String(item.id || '')
      if (!id || deletedRemoteIds.has(id)) continue
      const rec = mapRemoteUploadToRecordMeta(item)
      if (!Number((rec as any).productId || 0) && props.productId > 0) {
        ;(rec as any).productId = props.productId
        ;(rec as any).edits = (rec as any).edits || {}
        ;(rec as any).edits.preset = {
          ...(((rec as any).edits?.preset || {}) as any),
          productId: props.productId,
        }
      }
      if (known.has(id)) {
        const current = (pool.value || []).find((row) => String((row as any)?.id || '') === id) as any
        const merged = mergeServerAndLocalImageRecord((current || null) as any, rec as any)
        transientUploads.upsert(merged as any)
        pool.value = (pool.value || []).map((row) => String((row as any)?.id || '') === id ? merged as any : row)
        await ensurePreview(merged as any)
      } else {
        transientUploads.upsert(rec as any)
        pool.value.unshift(rec)
        await ensurePreview(rec)
        known.add(id)
      }
    }

    lastRemoteSyncAt.value = String(items[0]?.createdAt || lastRemoteSyncAt.value || '')
  } catch {
    // polling remains silent
  }
}

function startRemotePolling() {
  stopRemotePolling()
  remotePollTimer = setInterval(() => {
    syncRemoteProjectUploads().catch(() => {})
  }, 3500)
}

function stopRemotePolling() {
  if (remotePollTimer) clearInterval(remotePollTimer)
  remotePollTimer = null
}

async function startRealtimeUploadSubscription() {
  stopRealtimeUploadSubscription()
  const code = await project.ensureProject()
  if (!code) return
  if (uploadSource.value !== 'phone') {
    startRemotePolling()
    return
  }
  const cfg = getWidgetConfig() as any
  stopMercure = subscribeToUploadEvents({
    editorBaseUrl: cfg.editorBaseUrl,
    mercureHubUrl: cfg.mercureHubUrl,
    projectCode: code,
    categoryId: props.categoryId,
    onDeleted: async ({ ids }) => {
      markRemoteDeleted(ids)
      await syncRemoteProjectUploads()
    },
    onUpload: async (item) => {
      const id = String(item?.id || '')
      if (!id || wasRemoteDeleted(id)) return
      await syncRemoteProjectUploads()
    },
    onError: () => {
      startRemotePolling()
    },
  })

  if (!stopMercure) startRemotePolling()
}

function stopRealtimeUploadSubscription() {
  if (stopMercure) stopMercure()
  stopMercure = null
}

function presetOf(rec: any): any {
  return (rec?.edits?.preset || {}) as any
}

function sizeValue(rec: any): string {
  const p = presetOf(rec)
  const vid = String(p?.size?.variantId || '')
  if (vid && allSizeEntries.value.some((s) => s.key === vid || s.variantId === vid)) {
    const hit = allSizeEntries.value.find((s) => s.key === vid || s.variantId === vid)
    return String(hit?.key || vid)
  }
  const t = String(p?.size?.title || '')
  if (t) {
    const hit = allSizeEntries.value.find((s) => String(s.title) === t)
    if (hit) return String(hit.key)
  }
  return ''
}

function qtyValue(rec: any): number {
  const q = Number(presetOf(rec)?.quantity ?? 1)
  return q > 0 ? q : 1
}

function groupOptions(g: VariantGroup) {
  return (g?.options || []).map((o: any) => ({
    value: normId(o?.id || ''),
    label: String(o?.title || o?.value || '—'),
  })).filter((x: any) => !!x.value)
}

function groupValue(rec: any, groupId: string): string {
  const gid = normId(groupId)
  const opts = Array.isArray(presetOf(rec)?.options) ? presetOf(rec).options : []
  const hit = opts.find((o: any) => normId(o?.groupId || o?.id) === gid)
  return hit ? normId(hit.variantId || '') : ''
}

async function patchPreset(recId: string, patch: any) {
  const rec: any = await db.get(recId)
  if (!rec) return
  const prevPreset = (rec?.edits?.preset || {}) as any
  const nextPreset = { ...prevPreset, ...patch, productId: Number(props.productId || prevPreset?.productId || 0) }
  const next = {
    ...rec,
    productId: Number(props.productId || rec.productId || 0),
    edits: { ...(rec.edits || {}), preset: nextPreset },
    updatedAt: Date.now(),
  }
  await db.put(next as any)
  const idx = pool.value.findIndex((x) => x.id === recId)
  if (idx >= 0) pool.value[idx] = next as any
  cart.syncImageRecordIfInCart(next as any).catch(() => {})
}

async function onSizeChange(recId: string, sizeKey: string) {
  const hit = allSizeEntries.value.find((s) => s.key === sizeKey)
  if (!hit) {
    await patchPreset(recId, { size: null })
    return
  }
  const size: ImageSize = {
    title: hit.title,
    widthMm: hit.widthMm || undefined,
    heightMm: hit.heightMm || undefined,
    variantId: hit.variantId || undefined,
  }
  await patchPreset(recId, { size })
}

async function onGroupChange(recId: string, group: VariantGroup, variantId: string) {
  const gid = normId(group.id)
  const rec: any = await db.get(recId)
  if (!rec) return
  const p = presetOf(rec)
  const prev: any[] = Array.isArray(p?.options) ? p.options : []
  const keep = prev.filter((o: any) => normId(o?.groupId || o?.id) !== gid)
  if (variantId) {
    const sel = groupOptions(group).find((o: any) => o.value === variantId)
    keep.push({ groupId: gid, variantId, value: String(sel?.label || '') })
  }
  await patchPreset(recId, { options: keep })
}

async function onQtyChange(recId: string, qty: number) {
  const q = Math.max(1, Number(qty || 1))
  await patchPreset(recId, { quantity: q })
}

function selectFromPool(id: string) {
  emit('uploaded', [id])
}

async function duplicateOne(recId: string) {
  try {
    const rec: any = await db.get(recId)
    if (!rec) return
    const now = Date.now()
    const copy: any = {
      ...rec,
      id: uuidv4(),
      name: rec.name ? `${rec.name} (Kopie)` : 'Bild (Kopie)',
      createdAt: now,
      updatedAt: now,
      upload: { state: 'queued', tries: 0, updatedAt: now },
    }
    await db.put(copy)
    pool.value.unshift(copy)
    await ensurePreview(copy)
  } catch (e: any) {
    err.value = e?.message || 'Duplizieren fehlgeschlagen.'
  }
}

async function removeOne(recId: string) {
  deleteBusy.value = true
  try {
    deletedRemoteIds.add(String(recId))
    await deleteImageRecordsRemoteBulk([recId])
    transientUploads.remove(recId)
    await db.delete(recId).catch(() => {})
    revokePreview(recId)
    pool.value = pool.value.filter((x: any) => String(x?.id) !== String(recId))
    let handle: ReturnType<typeof setTimeout>
    handle = setTimeout(() => {
      deletedRemoteIds.delete(String(recId))
      deletedRemoteCleanupTimers.delete(handle)
    }, 60000)
    deletedRemoteCleanupTimers.add(handle)
  } catch (e: any) {
    if (isUploadInUseError(e)) {
      deletedRemoteIds.delete(String(recId))
      err.value = describeUploadInUse(e)
    } else {
      err.value = e?.message || 'Löschen fehlgeschlagen.'
    }
  } finally {
    deleteBusy.value = false
  }
}

async function addToCart(recId: string) {
  try {
    const rec: any = await db.get(recId)
    if (!rec) return
    await cart.syncImageRecord(rec as any)
    toast.success('In den Warenkorb gelegt')
  } catch (e: any) {
    err.value = e?.message || 'Konnte nicht in den Warenkorb legen.'
  }
}

async function onPick(e: Event) {
  const t = e.target as HTMLInputElement
  const files = t.files ? Array.from(t.files) : []
  t.value = ''
  if (!files.length) return
  await addFiles(files)
}

async function onDrop(e: DragEvent) {
  over.value = false
  const files = Array.from(e.dataTransfer?.files || []).filter((f) => isAcceptedImageUploadFile(f))
  if (!files.length) return
  await addFiles(files)
}

async function addFiles(files: File[]) {
  err.value = ''
  const accepted = files.filter((file) => isAcceptedImageUploadFile(file))
  if (accepted.length !== files.length) {
    err.value = `Einige Dateien wurden übersprungen. Erlaubt sind nur Bilder bis ${MAX_IMAGE_UPLOAD_MB} MB.`
  }
  if (!accepted.length) return
  const ids: string[] = []
  emitUploadEvent({ type: 'upload:planned', total: accepted.length })
  emitUploadEvent({ type: 'upload:preparing', delta: accepted.length })

  try {
    const categoryId = Number(props.categoryId || 0)
    const productId = Number(props.productId || 0)
    if (categoryId <= 0 || productId <= 0) {
      throw new Error('Upload-Kontext ungültig (Kategorie/Produkt fehlt).')
    }

    const projectCode = await project.ensureProject().catch(() => '')
    if (!projectCode) {
      throw new Error('Kein Projektcode verfügbar.')
    }

    const created: ImageRecord[] = []
    for (const file of accepted) {
      const id = uuidv4()
      const createdAt = Date.now()

      const rec: ImageRecord = {
        id,
        name: file.name || id,
        createdAt,
        updatedAt: createdAt,
        mime: String(file.type || 'application/octet-stream'),
        bytes: file.size,
        width: 0,
        height: 0,
        originalBlob: file,
        workingBlob: file,
        editorWidth: 0,
        editorHeight: 0,
        categoryId,
        productId,
        projectCode,
        edits: {
          preset: {
            productId,
            size: props.preset?.size,
            options: Array.isArray(props.preset?.options) ? props.preset.options : [],
            quantity: 1,
          } as any,
        },
        upload: {
          state: 'queued',
          phase: 'queued',
          progressPct: 0,
          uploadedBytes: 0,
          totalBytes: file.size,
          tries: 0,
          updatedAt: createdAt,
        } as any,
      }
      emitUploadEvent({ type: 'upload:preparing', delta: -1 })
      ids.push(id)
      pool.value.unshift(rec)
      transientUploads.upsert(rec as any)
      await ensurePreview(rec)
      created.push(rec)
      void readImageDimensions(file).then((resolved) => {
        const idx = pool.value.findIndex((x: any) => String(x?.id) === id)
        if (idx < 0) return
        pool.value[idx] = {
          ...(pool.value[idx] as any),
          width: resolved.width,
          height: resolved.height,
          editorWidth: resolved.width,
          editorHeight: resolved.height,
        } as any
      }).catch(() => {})
    }

    emit('uploaded', ids)
    const byId = new Map(created.map((rec) => [String(rec.id), rec]))
    void (async () => {
      try {
        const results = await uploadImageRecordsBatch(created, projectCode)
        const resultById = new Map(results.map((item) => [String(item.id), item]))
        for (const id of ids) {
          const current = byId.get(String(id))
          const hit = resultById.get(String(id))
          if (!current || !hit?.remoteUrl) continue
          revokePreview(id)
          const idx = pool.value.findIndex((x: any) => String(x?.id) === String(id))
          const next: any = {
            ...current,
            ...(hit.recordPatch || {}),
            originalBlob: undefined,
            workingBlob: undefined,
            editorBlob: undefined,
            thumbBlob: undefined,
            upload: {
              ...((current as any)?.upload || {}),
              state: 'done',
              phase: 'done',
              progressPct: 100,
              uploadedBytes: Number((current as any)?.bytes || 0) || 0,
              totalBytes: Number((current as any)?.bytes || 0) || 0,
              remoteUrl: hit.remoteUrl,
              updatedAt: Date.now(),
            },
          }
          if (idx >= 0) pool.value[idx] = next
          previewMap[id] = hit.remoteUrl
          transientUploads.upsert(next as any)
          emitUploadEvent({ type: 'upload:patched', id, patch: next })
        }
      } catch (e: any) {
        const failedIds = Array.isArray(e?.failedIds)
          ? e.failedIds.map((id: any) => String(id || '').trim()).filter(Boolean)
          : ids
        const partialById = new Map(
          Array.isArray(e?.partialResults)
            ? e.partialResults.map((item: any) => [String(item?.id || ''), item])
            : [],
        )
        for (const id of ids) {
          const current = byId.get(String(id))
          if (!current) continue
          const partial = partialById.get(String(id))
          const idx = pool.value.findIndex((x: any) => String(x?.id) === String(id))
          if (partial?.remoteUrl) {
            revokePreview(id)
            const next: any = {
              ...current,
              ...(partial.recordPatch || {}),
              originalBlob: undefined,
              workingBlob: undefined,
              editorBlob: undefined,
              thumbBlob: undefined,
              upload: {
                ...((current as any)?.upload || {}),
                state: 'done',
                phase: 'done',
                progressPct: 100,
                uploadedBytes: Number((current as any)?.bytes || 0) || 0,
                totalBytes: Number((current as any)?.bytes || 0) || 0,
                remoteUrl: partial.remoteUrl,
                updatedAt: Date.now(),
              },
            }
            if (idx >= 0) pool.value[idx] = next
            previewMap[id] = partial.remoteUrl
            transientUploads.upsert(next as any)
            emitUploadEvent({ type: 'upload:patched', id, patch: next })
            continue
          }
          if (!failedIds.includes(String(id))) continue
          if (idx >= 0) {
            pool.value[idx] = {
              ...(pool.value[idx] as any),
              upload: {
                ...(((pool.value[idx] as any)?.upload || {}) as any),
                state: 'failed',
                phase: 'failed',
                progressPct: 0,
                uploadedBytes: 0,
                totalBytes: Number((current as any)?.bytes || 0) || 0,
                lastError: String(e?.message || 'Upload fehlgeschlagen.'),
                updatedAt: Date.now(),
              },
            } as any
            transientUploads.patch(id, pool.value[idx] as any)
          }
        }
        err.value = e?.message || 'Upload fehlgeschlagen.'
      }
    })()
  } catch (e: any) {
    err.value = e?.message || 'Upload fehlgeschlagen.'
  }
}

watch(
  () => props.categoryId,
  async () => {
    await loadPool()
    await loadSizeOptionsFallback()
  },
  { immediate: true },
)

watch(
  () => [props.productId, (props.sizeOptions || []).length],
  async () => {
    await loadSizeOptionsFallback()
  },
  { immediate: true },
)

watch(
  () => uploadSource.value,
  async () => {
    await startRealtimeUploadSubscription()
  },
)

onMounted(async () => {
  await loadPool()
  if (isUnmounted.value) return
  await loadSizeOptionsFallback()
  if (isUnmounted.value) return
  await buildPhoneBridge()
  if (isUnmounted.value) return
  await syncRemoteProjectUploads()
  if (isUnmounted.value) return
  await startRealtimeUploadSubscription()
})

onBeforeUnmount(() => {
  isUnmounted.value = true
  stopRealtimeUploadSubscription()
  stopRemotePolling()
  revokeAllPreviews()
  for (const handle of deletedRemoteCleanupTimers) clearTimeout(handle)
  deletedRemoteCleanupTimers.clear()
})
</script>

<style scoped>
.u{ display:grid; gap:10px; position:relative; }
.delete-overlay{
  position:absolute;
  inset:0;
  z-index:5;
  background: rgba(255,255,255,.68);
  backdrop-filter: blur(2px);
  display:grid;
  place-items:center;
}
.delete-overlay__card{
  display:flex;
  align-items:center;
  gap:12px;
  padding:16px 20px;
  border-radius:4px;
  background:#fff;
  border:1px solid rgba(15,23,42,.08);
  box-shadow: 0 18px 40px rgba(15,23,42,.12);
}
.delete-overlay__spinner{
  width:18px;
  height:18px;
  border-radius:999px;
  border:2px solid rgba(15,23,42,.16);
  border-top-color:#e30b17;
  animation: upload-delete-spin .8s linear infinite;
}
@keyframes upload-delete-spin{
  to { transform: rotate(360deg); }
}
.source-switch{
  display:grid;
  grid-template-columns: 1fr 1fr;
  gap:8px;
}
.source-btn{
  height:36px;
  border:1px solid rgba(15,23,42,.18);
  background:#fff;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  gap:8px;
  font-size:12px;
  font-weight:700;
  cursor:pointer;
}
.source-btn[data-on="1"]{
  border-color:#111;
  background:rgba(15,23,42,.04);
}
.phone-box{
  display:grid;
  gap:12px;
  border:1px solid rgba(15,23,42,.14);
  background:#fafafa;
  padding:12px;
}
.phone-head{
  display:flex;
  align-items:flex-start;
  gap:10px;
}
.phone-title{ font-weight:700; }
.phone-sub{ font-size:12px; color:rgba(15,23,42,.62); margin-top:2px; }
.phone-qr{ display:flex; justify-content:center; }
.phone-qr img{
  width:220px;
  max-width:100%;
  display:block;
  background:#fff;
  border:1px solid rgba(15,23,42,.14);
  padding:8px;
}
.phone-actions{ display:flex; gap:8px; flex-wrap:wrap; }
.drop{
  border:2px dashed rgba(15,23,42,.20);
  background:#fafafa;
  padding:12px;
  cursor:pointer;
  user-select:none;
}
.drop.over{ border-color:#111; background:rgba(15,23,42,.04); }
.inner{ display:grid; gap:6px; text-align:center; }
.t{ color:#111; }
.s{ color:rgba(15,23,42,.60); font-size:12px; }
.err{ color:#b91c1c; font-size:13px; }

.pool{ border:1px solid rgba(15,23,42,.14); background:#fff; }
.pool-head{
  display:flex; align-items:center; justify-content:space-between;
  padding:8px 10px; border-bottom:1px solid rgba(15,23,42,.10); font-size:12px;
}
.pool-grid{
  display:grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap:10px;
  padding:10px;
  max-height: 58vh;
  overflow:auto;
}
.pool-item{ border:1px solid rgba(15,23,42,.12); background:#fff; padding:8px; display:grid; gap:8px; }
.pool-preview{ border:0; padding:0; background:#fff; cursor:pointer; }
.pool-preview img, .ph{ width:100%; aspect-ratio: 4 / 3; display:block; object-fit:cover; background:#f1f5f9; }
.pool-name{ font-size:12px; font-weight:700; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.row{ display:flex; gap:8px; align-items:center; }
.sel{ width:100%; height:32px; border:1px solid rgba(15,23,42,.18); background:#fff; font-size:12px; }
.qty-row{ justify-content:flex-end; }
.qty{
  width:74px;
  height:32px;
  border:1px solid rgba(15,23,42,.18);
  text-align:center;
  font-size:12px;
  appearance:textfield;
  -moz-appearance:textfield;
}
.qty::-webkit-outer-spin-button,
.qty::-webkit-inner-spin-button{
  -webkit-appearance:none;
  margin:0;
}
.act-row{ display:grid; grid-template-columns: 1fr 1fr; gap:8px; }
.btn{ height:32px; border:1px solid rgba(15,23,42,.18); background:#fff; font-size:12px; font-weight:700; cursor:pointer; }
.btn:hover{ background:#f8fafc; }
.icon-btn{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  padding:0;
}
.sel-flat{
  width:100%;
}
.sel-flat :deep(.fs-trigger){
  height:32px;
  border:1px solid rgba(15,23,42,.18);
  background:#fff;
}

@media (max-width: 680px) {
  .pool-grid{
    grid-template-columns: 1fr;
  }
}
</style>
